import { useEffect } from "react";

import { UserGeneralPermissions } from "@simplyk/common";
import { UseFormReturn, useWatch } from "react-hook-form";

import { RecipientContactList, RecipientContactLists } from "../Components/EmailRecipients/types";

import { useCurrentUserContext } from "@/common/contexts/CurrentUserContext";
import { orderListsForSuggestions } from "@/common/helpers/orderListsForSuggestions";
import { trpc } from "@/common/helpers/trpc";
import { EmailEditorFormData } from "@/common/types/email";

type UseSimpleEmailContactListsProps = {
  form: UseFormReturn<EmailEditorFormData>;
  emailContentId: string;
  enabled: boolean;
};

export const useSimpleEmailContactLists = ({
  form,
  emailContentId,
  enabled,
}: UseSimpleEmailContactListsProps): RecipientContactLists => {
  const { currentUser } = useCurrentUserContext();
  const canEdit = Boolean(currentUser?.newPermissions?.generalPermissions?.includes(UserGeneralPermissions.Contacts));

  const {
    data: linkedListsData,
    isFetching: isFetchingLinkedLists,
    isError: hasLinkedListsError,
  } = trpc.getEmailContentContactLists.useQuery(
    { emailContentId },
    { enabled: enabled && Boolean(emailContentId), refetchOnMount: "always" }
  );
  const { data: contactListsData } = trpc.getContactLists.useQuery(undefined, { enabled: enabled && canEdit });
  const selectedListIds = useWatch({ control: form.control, name: "contactListIds" });

  // The form only owns the list selection once the saved one is loaded, so a save never clears it by accident.
  useEffect(() => {
    if (canEdit && linkedListsData && !isFetchingLinkedLists && selectedListIds === undefined) {
      form.setValue(
        "contactListIds",
        linkedListsData.contactLists.map((list) => list.id)
      );
    }
  }, [canEdit, linkedListsData, isFetchingLinkedLists, selectedListIds, form]);

  const suggested: RecipientContactList[] = orderListsForSuggestions(
    contactListsData?.staticLists || [],
    contactListsData?.filterLists || []
  ).map((list) => ({ id: list.id, name: list.name, contactsCount: list.contactsCount }));
  const linkedLists: RecipientContactList[] = linkedListsData?.contactLists || [];
  const selected = (selectedListIds ?? linkedLists.map((list) => list.id)).map(
    (listId) =>
      suggested.find((list) => list.id === listId) ??
      linkedLists.find((list) => list.id === listId) ?? { id: listId, name: "" }
  );

  const setSelectedListIds = (listIds: string[]) => form.setValue("contactListIds", listIds, { shouldDirty: true });

  const isLinkedListsReady = Boolean(linkedListsData) && (!canEdit || selectedListIds !== undefined);

  return {
    status: !enabled || !emailContentId || isLinkedListsReady ? "ready" : hasLinkedListsError ? "error" : "loading",
    selected: enabled ? selected : [],
    suggested,
    canEdit: enabled && canEdit && selectedListIds !== undefined,
    onAdd: (list) => setSelectedListIds([...(selectedListIds ?? []), list.id]),
    onRemove: (listId) => setSelectedListIds((selectedListIds ?? []).filter((id) => id !== listId)),
    onClear: () => setSelectedListIds([]),
  };
};

export const getSimpleEmailRecipientsCount = (recipientEmails: string[], selectedLists: RecipientContactList[]) =>
  recipientEmails.length + selectedLists.reduce((total, list) => total + (list.contactsCount ?? 0), 0);
